import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { adminStoryService, AdminStoryDto, QueueStats } from '../../services/adminStoryService';
import { LoadingSpinner } from '../../shared/components';
import { Select, Tooltip } from '../../shared/components/UI';
import toast from 'react-hot-toast';

const ContentModerationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FiltersCard = styled.div`
  background: var(--color-white);
  padding: 1.5rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-gray-700);
`;

const FilterSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  background: var(--color-white);
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const ContentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ContentItem = styled.div<{ $priority: 'high' | 'medium' | 'low' }>`
  background: var(--color-white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  border-left: 4px solid ${props => 
    props.$priority === 'high' ? '#EF4444' :
    props.$priority === 'medium' ? '#F59E0B' :
    '#10B981'
  };
  overflow: hidden;
`;

const ContentHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-gray-200);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const ContentMeta = styled.div`
  flex: 1;
`;

const ContentTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-gray-900);
`;

const ContentInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--color-gray-600);
`;

const InfoItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ContentActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant: 'approve' | 'reject' | 'flag' | 'view' }>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.$variant) {
      case 'approve':
        return `
          background: #10B981;
          color: white;
          &:hover { background: #059669; }
        `;
      case 'reject':
        return `
          background: #EF4444;
          color: white;
          &:hover { background: #DC2626; }
        `;
      case 'flag':
        return `
          background: #F59E0B;
          color: white;
          &:hover { background: #D97706; }
        `;
      case 'view':
        return `
          background: var(--color-gray-100);
          color: var(--color-gray-700);
          border: 1px solid var(--color-gray-300);
          &:hover { background: var(--color-gray-200); }
        `;
    }
  }}
`;

const ContentBody = styled.div`
  padding: 1.5rem;
  background: var(--color-gray-50);
`;

const AIAnalysis = styled.div`
  background: var(--color-blue-50);
  border: 1px solid var(--color-blue-200);
  border-radius: var(--border-radius-md);
  padding: 1rem;
  margin-bottom: 1rem;
`;

const AIAnalysisHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--color-blue-700);
`;

const ConfidenceScore = styled.span<{ $score: number }>`
  padding: 0.25rem 0.5rem;
  border-radius: var(--border-radius-sm);
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: auto;
  
  ${props => {
    if (props.$score >= 0.8) {
      return `background: #FEE2E2; color: #991B1B;`;
    } else if (props.$score >= 0.5) {
      return `background: #FEF3C7; color: #92400E;`;
    } else {
      return `background: #D1FAE5; color: #065F46;`;
    }
  }}
`;

const ViolationTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const ViolationTag = styled.span`
  padding: 0.25rem 0.75rem;
  background: #FEE2E2;
  color: #991B1B;
  border-radius: var(--border-radius-full);
  font-size: 0.75rem;
  font-weight: 600;
`;

const ContentPreview = styled.div`
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--border-radius-md);
  padding: 1rem;
  max-height: 200px;
  overflow-y: auto;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--color-gray-700);
`;

const StatsBar = styled.div`
  background: var(--color-white);
  padding: 1rem 1.5rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-gray-800);
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--color-gray-600);
`;

interface ContentReportItem {
  id: string;
  type: 'story' | 'comment';
  title: string;
  author: string;
  reportedBy?: string;
  reportReason?: string;
  priority: 'high' | 'medium' | 'low';
  submittedAt: string;
  status: string;
  aiAnalysis?: {
    confidence: number;
    violations: string[];
    explanation: string;
    appropriate: boolean;
  };
  content: string;
  summary?: string;
}

const ContentModeration: React.FC = () => {
  const [contentReports, setContentReports] = useState<ContentReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [processingAction, setProcessingAction] = useState<string | null>(null);

  const [filters, setFilters] = useState({
    type: 'all',
    priority: 'all',
    status: 'pending'
  });

  useEffect(() => {
    loadContentReports();
    loadQueueStats();
  }, [filters.status]);

  const loadContentReports = async () => {
    try {
      setLoading(true);
      const response = await adminStoryService.getPendingStories({
        status: filters.status === 'pending' ? undefined : filters.status,
        size: 50
      });
      
      const transformedReports = (response.content || []).map((story: AdminStoryDto) => ({
        id: story.id,
        type: 'story' as const,
        title: story.title,
        author: story.authorId, // TODO: Fetch actual username
        reportedBy: 'System AI',
        reportReason: story.aiAnalysis && !story.aiAnalysis.appropriate ? 'AI detected inappropriate content' : 'Pending review',
        priority: story.urgency as 'high' | 'medium' | 'low',
        submittedAt: story.submittedAt,
        status: story.status,
        aiAnalysis: story.aiAnalysis ? {
          confidence: story.aiAnalysis.confidence,
          violations: story.aiAnalysis.violations,
          explanation: story.aiAnalysis.explanation,
          appropriate: story.aiAnalysis.appropriate
        } : undefined,
        content: story.content || 'No content preview available',
        summary: story.summary
      }));
      
      setContentReports(transformedReports);
    } catch (error) {
      console.error('Failed to load content reports:', error);
      toast.error('Failed to load content reports');
    } finally {
      setLoading(false);
    }
  };
  
  const loadQueueStats = async () => {
    try {
      const stats = await adminStoryService.getQueueStats();
      setQueueStats(stats);
    } catch (error) {
      console.error('Failed to load queue stats:', error);
    }
  };

  const filteredReports = contentReports.filter(report => {
    return (
      (filters.type === 'all' || report.type === filters.type) &&
      (filters.priority === 'all' || report.priority === filters.priority)
    );
  });

  const handleContentAction = async (id: string, action: 'approve' | 'reject' | 'flag') => {
    try {
      setProcessingAction(id);
      
      if (action === 'approve') {
        await adminStoryService.approveStory(id, 'Content approved by moderator');
        toast.success('✅ Content approved successfully!');
      } else if (action === 'reject') {
        await adminStoryService.rejectStory(id, 'Content rejected by moderator');
        toast.success('❌ Content removed successfully!');
      } else if (action === 'flag') {
        await adminStoryService.triggerAiReview(id);
        toast.success('🚨 Content flagged for review!');
      }
      
      // Reload content reports and stats
      await Promise.all([loadContentReports(), loadQueueStats()]);
      
    } catch (error) {
      console.error(`Failed to ${action} content:`, error);
      toast.error(`Failed to ${action} content`);
    } finally {
      setProcessingAction(null);
    }
  };

  const stats = queueStats || {
    totalPending: 0,
    adminReviewPending: 0,
    appealPending: 0,
    urgent: 0
  };

  const derivedStats = {
    totalReports: stats.totalPending + (stats.adminReviewPending || 0) + (stats.appealPending || 0),
    pendingReview: stats.totalPending,
    autoResolved: 0, // This would need a separate endpoint
    falsePositives: 0 // This would need a separate endpoint
  };

  return (
    <ContentModerationContainer>
      {/* Stats Bar */}
      <StatsBar>
        <StatItem>
          <StatValue>{derivedStats.totalReports}</StatValue>
          <StatLabel>Total Reports</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{derivedStats.pendingReview}</StatValue>
          <StatLabel>Pending Review</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{stats.urgent}</StatValue>
          <StatLabel>Urgent Items</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{stats.appealPending}</StatValue>
          <StatLabel>Appeals</StatLabel>
        </StatItem>
      </StatsBar>

      {/* Filters */}
      <FiltersCard>
        <Select
          label="Content Type"
          value={filters.type}
          onChange={(value) => setFilters({ ...filters, type: value })}
          options={[
            { value: 'all', label: 'All Types' },
            { value: 'story', label: 'Stories' },
            { value: 'comment', label: 'Comments' }
          ]}
        />

        <Select
          label="Priority"
          value={filters.priority}
          onChange={(value) => setFilters({ ...filters, priority: value })}
          options={[
            { value: 'all', label: 'All Priorities' },
            { value: 'high', label: 'High' },
            { value: 'medium', label: 'Medium' },
            { value: 'low', label: 'Low' }
          ]}
        />

        <Select
          label="Status"
          value={filters.status}
          onChange={(value) => setFilters({ ...filters, status: value })}
          options={[
            { value: 'pending', label: 'Pending' },
            { value: 'reviewed', label: 'Reviewed' },
            { value: 'resolved', label: 'Resolved' }
          ]}
        />

        <Tooltip content="Perform actions on multiple items at once" position="top">
          <button className="btn btn-primary">
            Bulk Actions
          </button>
        </Tooltip>
      </FiltersCard>

      {/* Content Reports */}
      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: '3rem',
          background: 'var(--color-white)',
          borderRadius: 'var(--border-radius-lg)',
          boxShadow: 'var(--shadow-md)'
        }}>
          <LoadingSpinner message="Loading content reports..." />
        </div>
      ) : (
      <ContentList>
        {filteredReports.map(report => (
          <ContentItem key={report.id} $priority={report.priority}>
            <ContentHeader>
              <ContentMeta>
                <ContentTitle>{report.title}</ContentTitle>
                <ContentInfo>
                  <InfoItem>
                    <span>📝</span>
                    {report.type}
                  </InfoItem>
                  <InfoItem>
                    <span>👤</span>
                    By {report.author}
                  </InfoItem>
                  <InfoItem>
                    <span>🚨</span>
                    Reported by {report.reportedBy}
                  </InfoItem>
                  <InfoItem>
                    <span>🕒</span>
                    {new Date(report.submittedAt).toLocaleString()}
                  </InfoItem>
                  <InfoItem>
                    <span>📋</span>
                    {report.reportReason}
                  </InfoItem>
                </ContentInfo>
              </ContentMeta>

              <ContentActions>
                <Tooltip content="View the full content details" position="top">
                  <ActionButton
                    $variant="view"
                    onClick={() => console.log('View full content')}
                  >
                    View Full
                  </ActionButton>
                </Tooltip>
                <Tooltip content="Approve and publish this content" position="top">
                  <ActionButton
                    $variant="approve"
                    onClick={() => handleContentAction(report.id, 'approve')}
                    disabled={processingAction === report.id}
                  >
                    {processingAction === report.id ? '⏳' : '✅'} Approve
                  </ActionButton>
                </Tooltip>
                <Tooltip content="Remove this content permanently" position="top">
                  <ActionButton
                    $variant="reject"
                    onClick={() => handleContentAction(report.id, 'reject')}
                    disabled={processingAction === report.id}
                  >
                    {processingAction === report.id ? '⏳' : '❌'} Remove
                  </ActionButton>
                </Tooltip>
                <Tooltip content="Flag for additional review" position="top">
                  <ActionButton
                    $variant="flag"
                    onClick={() => handleContentAction(report.id, 'flag')}
                    disabled={processingAction === report.id}
                  >
                    {processingAction === report.id ? '⏳' : '🚨'} Flag
                  </ActionButton>
                </Tooltip>
              </ContentActions>
            </ContentHeader>

            <ContentBody>
              {report.aiAnalysis && (
                <AIAnalysis>
                  <AIAnalysisHeader>
                    <span>🤖</span>
                    AI Analysis
                    <ConfidenceScore $score={report.aiAnalysis.confidence}>
                      {Math.round(report.aiAnalysis.confidence * 100)}% confidence
                    </ConfidenceScore>
                  </AIAnalysisHeader>

                  <ViolationTags>
                    {report.aiAnalysis.violations.map(violation => (
                      <ViolationTag key={violation}>
                        {violation.replace('_', ' ')}
                      </ViolationTag>
                    ))}
                  </ViolationTags>

                  <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-blue-700)' }}>
                    {report.aiAnalysis.explanation}
                  </p>
                </AIAnalysis>
              )}

              <ContentPreview>
                {report.content}
              </ContentPreview>
            </ContentBody>
          </ContentItem>
        ))}
        
        {filteredReports.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '3rem', 
            color: 'var(--color-gray-500)',
            background: 'var(--color-white)',
            borderRadius: 'var(--border-radius-lg)',
            boxShadow: 'var(--shadow-md)'
          }}>
            🎉 No content reports match the current filters.
          </div>
        )}
      </ContentList>
      )}
    </ContentModerationContainer>
  );
};

export default ContentModeration;